import { useState } from 'react';
import { CaretIcon } from '@rsos/assets/icons';
import { Text } from '@rsos/base-components';
import type {
  ConsistCar,
  ConsistHazmatSource,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import { formatCount } from '../../../../../utils/norfolkSouthern/format';
import styles from './NorfolkSouthernStatus.module.css';
import { formatCarHeading } from './norfolkSouthernStatusUtils';

interface ErgGuideLinkProps {
  source: ConsistHazmatSource;
}

/**
 * "ERG Guide: N" click through to the Emergency Response Guidebook page for
 * a hazmat source, the same affordance the train card's hazmat header has.
 */
function ErgGuideLink({ source }: ErgGuideLinkProps): JSX.Element {
  return (
    <a
      className={styles.ergLink}
      href={source.ergUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-name="norfolk-southern-erg-link"
    >
      ERG Guide: {source.ergGuideNumber ?? 'Unknown'}
    </a>
  );
}

interface HazmatSourceDetailsProps {
  source: ConsistHazmatSource;
}

function HazmatSourceDetails({ source }: HazmatSourceDetailsProps) {
  const rows: { label: string; value: string | null | undefined }[] = [
    { label: 'Shipping name', value: source.properShippingName },
    { label: 'Technical name', value: source.technicalName },
    { label: 'UN/NA', value: source.unNaId },
    { label: 'Hazard class', value: source.hazardClass },
    { label: 'Packing group', value: source.packingGroup },
    { label: 'Inhalation hazard', value: source.inhalationHazard },
    {
      label: 'Emergency contact',
      value: [source.emergencyContactName, source.emergencyContactNumber]
        .filter(Boolean)
        .join(' '),
    },
  ];

  return (
    <>
      {rows
        .filter(row => row.value)
        .map(row => (
          <div key={row.label} className={styles.infoRow}>
            <span className={styles.infoLabel}>{row.label}</span>
            <span className={styles.infoValue}>{row.value}</span>
          </div>
        ))}
    </>
  );
}

interface ConsistCarRowProps {
  car: ConsistCar;
}

/**
 * One car in the consist: reporting marks, what it carries, and the ERG
 * guide for any hazmat aboard, with the full detail behind a toggle.
 */
function ConsistCarRow({ car }: ConsistCarRowProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);

  const commodity = car.loaded ? (car.commodity ?? 'Loaded') : 'Empty';
  const allSources = [
    ...car.hazmatSources,
    ...car.containers.flatMap(container => container.hazmatSources),
  ];
  const hasDetails =
    allSources.length > 0 ||
    car.containers.length > 0 ||
    !!car.carType ||
    typeof car.quantity === 'number';

  return (
    <div
      className={styles.carRow}
      data-name="norfolk-southern-consist-car"
      data-hazmat={car.hazmat ? 'true' : 'false'}
    >
      <div className={styles.carRowHeader}>
        <div className={styles.carRowSummary}>
          <Text color="primaryText" size="small" fontWeight="bold">
            {formatCarHeading(car)}
          </Text>
          {car.containers.length === 0 && (
            <span className={styles.carRowMeta}>{commodity}</span>
          )}
          {car.hazmatSources.map((source, index) => (
            <ErgGuideLink key={`car-${index}`} source={source} />
          ))}
          {car.containers.map((container, index) => (
            <div
              key={`${container.containerId}-${index}`}
              className={styles.containerBlock}
            >
              <Text color="primaryText" size="small">
                Container {container.containerId}
              </Text>
              <span className={styles.carRowMeta}>
                {container.commodity ?? 'No commodity reported'}
              </span>
              {container.hazmatSources.map((source, sourceIndex) => (
                <ErgGuideLink
                  key={`container-${index}-${sourceIndex}`}
                  source={source}
                />
              ))}
            </div>
          ))}
        </div>
        {hasDetails && (
          <button
            type="button"
            className={styles.carRowToggle}
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Hide' : 'Show'} details for ${car.reportingMarks}`}
            onClick={() => setIsExpanded(prev => !prev)}
            data-name="norfolk-southern-consist-car-toggle"
          >
            <span
              className={`${styles.caret}${isExpanded ? '' : ` ${styles.caretCollapsed}`}`}
            >
              <CaretIcon color="primaryText" width={11} height={8} />
            </span>
          </button>
        )}
      </div>

      {isExpanded && (
        <div
          className={styles.carRowDetails}
          data-name="norfolk-southern-consist-car-details"
        >
          {car.carType && (
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Car type</span>
              <span className={styles.infoValue}>{car.carType}</span>
            </div>
          )}
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Load</span>
            <span className={styles.infoValue}>{commodity}</span>
          </div>
          {typeof car.quantity === 'number' && (
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Quantity</span>
              <span className={styles.infoValue}>
                {formatCount(car.quantity)}
              </span>
            </div>
          )}
          {allSources.map((source, index) => (
            <HazmatSourceDetails key={index} source={source} />
          ))}
        </div>
      )}
    </div>
  );
}

export default ConsistCarRow;
